
(() => {
  'use strict';

  const init = (root) => {
    if (root.dataset.productStoryReady === 'true') return;
    root.dataset.productStoryReady = 'true';

    const form = root.querySelector('[data-op-form]');
    const radios = form ? Array.from(form.querySelectorAll('input[type="radio"][name="id"]')) : [];
    const submitButtons = Array.from(root.querySelectorAll('[data-op-submit]'));
    const quantity = form?.querySelector('input[name="quantity"]');
    const minus = root.querySelector('[data-op-quantity-minus]');
    const plus = root.querySelector('[data-op-quantity-plus]');
    const toast = root.querySelector('[data-op-toast]');
    const toastMessage = toast?.querySelector('[data-op-message]');
    const toastCartLink = toast?.querySelector('a');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const labels = {
      add: root.dataset.addLabel || 'Add to cart',
      sold: root.dataset.soldLabel || 'Sold out',
      adding: root.dataset.addingLabel || 'Adding…',
      added: root.dataset.addedLabel || 'Added to your cart',
      error: root.dataset.errorLabel || 'We could not confirm this item was added. Please try again.',
    };
    let adding = false;

    const selectedVariant = () => radios.find((radio) => radio.checked);

    const updateButtons = () => {
      const selected = selectedVariant();
      const available = Boolean(selected && selected.dataset.available !== 'false');
      const label = adding ? labels.adding : available ? labels.add : labels.sold;
      submitButtons.forEach((button) => {
        button.disabled = adding || !available;
        button.setAttribute('aria-busy', String(adding));
        const text = button.querySelector('[data-op-submit-text]');
        if (text) text.textContent = label;
        else button.textContent = label;
      });
      root.classList.toggle('op-is-adding', adding);
    };

    const updateVariant = (updateURL = false) => {
      const selected = selectedVariant();
      if (!selected) return;
      root.querySelectorAll('[data-op-price]').forEach((price) => {
        if (selected.dataset.price) price.textContent = selected.dataset.price;
      });
      root.querySelectorAll('[data-op-selected]').forEach((label) => {
        label.textContent = selected.dataset.title || selected.value;
      });
      updateButtons();
      if (updateURL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selected.value);
        window.history.replaceState(window.history.state, '', url);
      }
    };

    const selectVariantFromURL = () => {
      const variantID = new URL(window.location.href).searchParams.get('variant');
      const match = radios.find((radio) => radio.value === variantID);
      if (match) match.checked = true;
      if (!selectedVariant() && radios.length) radios[0].checked = true;
      updateVariant();
    };

    radios.forEach((radio) => radio.addEventListener('change', () => updateVariant(true)));
    window.addEventListener('popstate', selectVariantFromURL);
    selectVariantFromURL();

    const normalizeQuantity = (value = quantity?.value) => {
      if (!quantity) return 1;
      const number = Number(value);
      const normalized = Number.isFinite(number) ? Math.min(99, Math.max(1, Math.floor(number))) : 1;
      quantity.value = String(normalized);
      if (minus) minus.disabled = normalized <= 1;
      if (plus) plus.disabled = normalized >= 99;
      return normalized;
    };

    if (quantity) {
      quantity.min = '1';
      quantity.max = '99';
      quantity.step = '1';
      normalizeQuantity();
      quantity.addEventListener('change', () => normalizeQuantity());
      minus?.addEventListener('click', (event) => {
        event.preventDefault();
        normalizeQuantity(normalizeQuantity() - 1);
      });
      plus?.addEventListener('click', (event) => {
        event.preventDefault();
        normalizeQuantity(normalizeQuantity() + 1);
      });
    }

    const showToast = (message, success) => {
      if (!toast || !toastMessage) return;
      toastMessage.textContent = message;
      toast.dataset.state = success ? 'success' : 'error';
      if (toastCartLink) toastCartLink.hidden = false;
      toast.hidden = false;
    };

    toast?.querySelector('[data-op-toast-close]')?.addEventListener('click', () => {
      toast.hidden = true;
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (adding) return;
      const selected = selectedVariant();
      if (!selected || selected.dataset.available === 'false') return;
      normalizeQuantity();
      if (!form.reportValidity()) return;
      adding = true;
      updateButtons();
      if (toast) toast.hidden = true;

      try {
        const data = new FormData(form);

        data.set('id', selected.value);
        const routeRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${routeRoot.replace(/\/?$/, '/')}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const result = await response.json();
        const validItem = (item) => item && (typeof item.id === 'number' || typeof item.id === 'string') && Number(item.quantity) > 0;
        const confirmed = validItem(result) || (Array.isArray(result?.items) && result.items.length > 0 && result.items.every(validItem));
        if (!response.ok || result?.status >= 400 || !confirmed) throw new Error('Cart addition was not confirmed.');
        showToast(labels.added, true);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'product-story', items: result.items || [result] } }));
      } catch (_) {
        showToast(labels.error, false);
      } finally {
        adding = false;
        updateButtons();
      }
    });

    const track = root.querySelector('[data-op-track]');
    const slides = track ? Array.from(track.querySelectorAll('[data-op-slide]')) : [];
    const thumbs = Array.from(root.querySelectorAll('[data-op-thumb]'));
    const previous = root.querySelector('[data-op-prev]');
    const next = root.querySelector('[data-op-next]');
    const count = root.querySelector('[data-op-count]');
    const total = root.querySelector('[data-op-total]');
    let activeSlide = 0;
    let scrollFrame = null;

    const updateGallery = (index) => {
      activeSlide = Math.min(Math.max(index, 0), Math.max(slides.length - 1, 0));
      if (count) count.textContent = String(activeSlide + 1);
      if (total) total.textContent = String(slides.length);
      if (previous) previous.disabled = activeSlide === 0;
      if (next) next.disabled = activeSlide === slides.length - 1;
      thumbs.forEach((thumb) => {
        if (Number(thumb.dataset.index) === activeSlide) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };

    const goToSlide = (index) => {
      if (!track || !slides.length) return;
      const target = Math.min(Math.max(index, 0), slides.length - 1);
      const left = slides[target].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      updateGallery(target);
    };

    if (track && slides.length) {
      track.scrollLeft = 0;
      updateGallery(0);
      track.addEventListener('scroll', () => {
        if (scrollFrame !== null) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = null;
          const left = track.getBoundingClientRect().left;
          let nearest = 0;
          let distance = Infinity;
          slides.forEach((slide, index) => {
            const delta = Math.abs(slide.getBoundingClientRect().left - left);
            if (delta < distance) { nearest = index; distance = delta; }
          });
          updateGallery(nearest);
        });
      }, { passive: true });
      previous?.addEventListener('click', () => goToSlide(activeSlide - 1));
      next?.addEventListener('click', () => goToSlide(activeSlide + 1));
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => goToSlide(Number(thumb.dataset.index))));
    }

    const dialog = root.querySelector('[data-op-dialog]');
    const zoomImage = dialog?.querySelector('[data-op-zoom-image]');
    let zoomTrigger = null;
    if (dialog && zoomImage && typeof dialog.showModal === 'function') {
      slides.forEach((slide) => slide.addEventListener('click', () => {
        if (!slide.dataset.full) return;
        zoomTrigger = slide;
        zoomImage.src = slide.dataset.full;
        zoomImage.alt = slide.dataset.alt || '';
        if (!dialog.open) dialog.showModal();
      }));
      dialog.querySelector('[data-op-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        zoomTrigger?.focus({ preventScroll: true });
        zoomTrigger = null;
      });
    }

    const features = Array.from(root.querySelectorAll('details[data-op-feature]'));
    const featureImages = Array.from(root.querySelectorAll('[data-op-feature-image]'));
    const showFeature = (index) => {
      featureImages.forEach((image) => { image.hidden = Number(image.dataset.index) !== index; });
    };
    features.forEach((feature) => feature.addEventListener('toggle', () => {
      if (!feature.open) return;
      features.forEach((other) => { if (other !== feature) other.open = false; });
      showFeature(Number(feature.dataset.index));
    }));
    showFeature(Number(features.find((feature) => feature.open)?.dataset.index || 0));

    const sticky = root.querySelector('[data-op-sticky]');
    if (sticky && form && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !visible;
        root.classList.toggle('op-sticky-visible', visible);
      }, { threshold: 0 });
      observer.observe(form);
    }
  };

  const boot = () => document.querySelectorAll('[data-product-story]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
(() => {
 const bootModels=()=>document.querySelectorAll('[data-ps-model]').forEach(root=>{
  if(root.dataset.bound)return;root.dataset.bound='true';
  const start=root.querySelector('[data-model-start]'),stage=root.querySelector('[data-model-stage]'),status=root.querySelector('[data-model-status]');
  let pending=false;
  const load=async()=>{
   if(pending||root.dataset.modelState==='ready')return;
   pending=true;start.hidden=true;start.disabled=true;stage.setAttribute('aria-busy','true');
   root.dataset.modelState='loading';status.textContent=root.dataset.loading;
   try{const module=await import(root.dataset.viewerUrl);await module.mount(root);}
   catch(_){root.dataset.modelState='error';status.textContent=root.dataset.error;start.hidden=false;start.disabled=false;}
   finally{pending=false;stage.setAttribute('aria-busy','false');}
  };
  start?.addEventListener('click',load);

 });
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',bootModels,{once:true});else bootModels();
 document.addEventListener('shopify:section:load',bootModels);
})();
